/**
 * Categorize Debt Priority — Plan Payment Order section 2
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSettlementSession } from '../../../hooks/useSettlementSession';
import { useSettlement } from '../../../contexts/SettlementContext';
import {
  PLAN_PAYMENT_SPEC_DEBT_PRIORITY,
  updatePlanPaymentSectionFromChecks,
} from './planPaymentHub';

const CHILD_SPEC = PLAN_PAYMENT_SPEC_DEBT_PRIORITY;
const FRAME_ID = CHILD_SPEC.frameId;

const ITEMS = [
  {
    id: 'pp-s2-admin',
    title: 'Flag Administrative & Funeral Expenses',
    description:
      'These are typically the highest priority: court fees, attorney fees, funeral costs, and estate administration expenses.',
  },
  {
    id: 'pp-s2-tax',
    title: 'Identify Tax Obligations',
    description:
      'Federal and state income taxes, property taxes, and any estate taxes due take priority over general unsecured creditors.',
  },
  {
    id: 'pp-s2-general',
    title: 'Classify General Creditors',
    description:
      'Credit cards, personal loans, and medical bills are typically in the lowest priority tier for payment.',
  },
] as const;

export default function PlanPayment_DebtPriority() {
  const { estateId } = useParams<{ estateId: string }>();
  const navigate = useNavigate();
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData, updateStepProgress, isLoading, error } = useSettlementSession(
    estateId ?? null,
    { readOnly: isArchived }
  );

  const [checks, setChecks] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(ITEMS.map((i) => [i.id, false]))
  );

  const reload = useCallback(async () => {
    if (!estateId) return;
    const framed = await loadFrameData(FRAME_ID);
    const rawChecks = framed?.data?.checks as Record<string, boolean> | undefined;
    setChecks(Object.fromEntries(ITEMS.map(({ id }) => [id, !!rawChecks?.[id]])));
  }, [estateId, loadFrameData]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const backPath = estateId ? `/sframe-202/${estateId}` : '/sframe-202';

  const persistChecks = async (next: Record<string, boolean>) => {
    if (!estateId || isArchived) return;
    const framed = await loadFrameData(FRAME_ID);
    const prev =
      typeof framed?.data === 'object' && framed?.data ? (framed.data as Record<string, unknown>) : {};
    await saveFrameData(FRAME_ID, { ...prev, checks: next });
    setChecks(next);
    await updatePlanPaymentSectionFromChecks(
      loadFrameData,
      saveFrameData,
      updateStepProgress,
      CHILD_SPEC,
      next
    );
  };

  const toggle = (itemId: string) => {
    const next = { ...checks, [itemId]: !checks[itemId] };
    void persistChecks(next);
  };

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => navigate(backPath)}
        className="block text-left text-stone-500 hover:text-stone-700 mb-6 text-sm"
      >
        ← Plan Payment Order
      </button>

      <h1 className="text-2xl font-semibold text-stone-800 mb-6">Categorize Debt Priority</h1>

      <div className="space-y-3">
        {ITEMS.map((item) => (
          <div
            key={item.id}
            className="bg-white rounded-xl border border-stone-200 p-5 flex items-start gap-4"
          >
            <input
              type="checkbox"
              checked={!!checks[item.id]}
              disabled={isLoading || isArchived}
              onChange={() => toggle(item.id)}
              className="mt-1 h-5 w-5 rounded border-stone-300 text-cyan-600 cursor-pointer disabled:opacity-50"
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-stone-800">{item.title}</p>
              <p className="text-stone-500 text-sm mt-1">{item.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
